import type { vec3 } from 'gl-matrix';
import { Modality } from '~/domain/dicom/modality';
import {
    assertValidCheckerboardTileSizeMm,
    DEFAULT_CHECKERBOARD_TILE_SIZE_MM,
} from './checkerboardTileSize';

/**
 * A colour pair a fused pair of non-PET images is tinted with: the primary
 * takes one, the secondary the other, so the two can be told apart where
 * they overlap. The preview colours are for the picker's swatch only.
 */
export interface FusionColorMapping {
    readonly id: string;
    readonly name: string;
    readonly primaryValues: vec3;
    readonly secondaryValues: vec3;
    readonly previewPrimary: string;
    readonly previewSecondary: string;
}

export const grayscaleFusionColorMapping: FusionColorMapping = {
    id: 'noColorMapping',
    name: 'Grayscale',
    primaryValues: [1.0, 1.0, 1.0],
    secondaryValues: [1.0, 1.0, 1.0],
    // Neither image is tinted, so the swatch pictures the grey ramp both
    // are drawn through rather than a pair of tints.
    previewPrimary: '#000000',
    previewSecondary: '#FFFFFF',
};

const cyanMagentaFusionColorMapping: FusionColorMapping = {
    id: 'cyanMagenta',
    name: 'Cyan–Magenta',
    primaryValues: [0.0, 1.0, 1.0],
    secondaryValues: [1.0, 0.0, 1.0],
    previewPrimary: '#00FFFF',
    previewSecondary: '#FF00FF',
};

/** The CSS gradient that pictures a colour pair: the primary's tint running into the secondary's. */
export function pairGradient(primary: string, secondary: string): string {
    return `linear-gradient(to right, ${primary}, ${secondary})`;
}

/** Every colour pair offered, grayscale first. */
export const FUSION_COLOR_MAPPINGS: ReadonlyArray<FusionColorMapping> = [
    grayscaleFusionColorMapping,
    cyanMagentaFusionColorMapping,
    {
        id: 'orangeBlue',
        name: 'Orange–Blue',
        primaryValues: [1.0, 0.55, 0.0],
        secondaryValues: [0.0, 0.47, 1.0],
        previewPrimary: '#FF8C00',
        previewSecondary: '#0077FF',
    },
    {
        id: 'greenPurple',
        name: 'Green–Purple',
        primaryValues: [0.6, 1.0, 0.6],
        secondaryValues: [0.8, 0.6, 1.0],
        previewPrimary: '#99FF99',
        previewSecondary: '#CC99FF',
    },
    {
        id: 'yellowCyan',
        name: 'Yellow–Cyan',
        primaryValues: [1.0, 1.0, 0.6],
        secondaryValues: [0.6, 1.0, 1.0],
        previewPrimary: '#FFFF99',
        previewSecondary: '#99FFFF',
    },
    {
        id: 'fireIce',
        name: 'Fire–Ice',
        primaryValues: [1.0, 0.27, 0.0],
        secondaryValues: [0.0, 0.75, 1.0],
        previewPrimary: '#FF4500',
        previewSecondary: '#00BFFF',
    },
];

/** The colour pair with this id. Throws on an id no picker offers. */
export function fusionColorMappingById(id: string): FusionColorMapping {
    const mapping = FUSION_COLOR_MAPPINGS.find((m) => m.id === id);
    if (!mapping) {
        throw new Error(`Unknown fusion colour mapping: ${id}.`);
    }
    return mapping;
}

/**
 * A colour ramp a PET image is drawn through, as stops from no uptake
 * (position 0) to the window's top (position 1). The id is the ramp's
 * index in the renderer's colormap table.
 */
export interface PetColorMap {
    readonly id: number;
    readonly name: string;
    readonly stops: ReadonlyArray<{
        readonly position: number;
        readonly color: readonly [number, number, number];
    }>;
    readonly previewGradient: string;
}

const defaultPetColorMap: PetColorMap = {
    id: 0,
    name: 'Hot Iron',
    stops: [
        { position: 0.0, color: [0.0, 0.0, 0.0] },
        { position: 0.333, color: [1.0, 0.0, 0.0] },
        { position: 0.667, color: [1.0, 1.0, 0.0] },
        { position: 1.0, color: [1.0, 1.0, 1.0] },
    ],
    previewGradient:
        'linear-gradient(to right, #000000 0%, #ff0000 33%, #ffff00 67%, #ffffff 100%)',
};

/** Every PET ramp offered, in the renderer's table order. */
export const PET_COLOR_MAPS: ReadonlyArray<PetColorMap> = [
    defaultPetColorMap,
    {
        id: 1,
        name: 'Green Fire',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.0] },
            { position: 0.333, color: [0.0, 1.0, 0.0] },
            { position: 0.667, color: [1.0, 1.0, 0.0] },
            { position: 1.0, color: [1.0, 1.0, 1.0] },
        ],
        previewGradient:
            'linear-gradient(to right, #000000 0%, #00ff00 33%, #ffff00 67%, #ffffff 100%)',
    },
    {
        id: 2,
        name: 'Rainbow',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.0] },
            { position: 0.25, color: [0.0, 0.0, 1.0] },
            { position: 0.5, color: [0.0, 1.0, 1.0] },
            { position: 0.75, color: [1.0, 1.0, 0.0] },
            { position: 1.0, color: [1.0, 0.0, 0.0] },
        ],
        previewGradient:
            'linear-gradient(to right, #000000 0%, #0000ff 25%, #00ffff 50%, #ffff00 75%, #ff0000 100%)',
    },
    {
        id: 3,
        name: 'Rainbow 2',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.04] },
            { position: 0.25, color: [0.32, 0.06, 0.44] },
            { position: 0.5, color: [0.73, 0.21, 0.33] },
            { position: 0.75, color: [0.99, 0.56, 0.15] },
            { position: 1.0, color: [0.99, 0.99, 0.64] },
        ],
        previewGradient:
            'linear-gradient(to right, #00000a 0%, #520b70 25%, #ba3654 50%, #fc8f26 75%, #fcfca3 100%)',
    },
    {
        id: 4,
        name: 'Spectrum',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.0] },
            { position: 0.1, color: [0.27, 0.0, 0.33] },
            { position: 0.325, color: [0.28, 0.14, 0.47] },
            { position: 0.55, color: [0.13, 0.37, 0.52] },
            { position: 0.775, color: [0.37, 0.65, 0.35] },
            { position: 1.0, color: [0.99, 0.91, 0.14] },
        ],
        previewGradient:
            'linear-gradient(to right, #000000 0%, #440054 10%, #472478 32.5%, #215e85 55%, #5ea659 77.5%, #fce823 100%)',
    },
    {
        id: 5,
        name: 'Invert Gray',
        stops: [
            { position: 0.0, color: [1.0, 1.0, 1.0] },
            { position: 1.0, color: [0.0, 0.0, 0.0] },
        ],
        previewGradient: 'linear-gradient(to right, #ffffff 0%, #000000 100%)',
    },
];

/** The PET ramp with this id. Throws on an id no picker offers. */
export function petColorMapById(id: number): PetColorMap {
    const colorMap = PET_COLOR_MAPS.find((m) => m.id === id);
    if (!colorMap) {
        throw new Error(`Unknown PET colour map: ${id}.`);
    }
    return colorMap;
}

/** How a fused pair is drawn together: mixed by alpha, or interleaved as tiles. */
export type FusionViewMode = 'blend' | 'checkerboard';

/**
 * Whether a pair of these modalities holds a PET image. Such a pair is
 * drawn through the PET ramp, which the renderer applies only over a
 * blend: it is never interleaved, and its colours are the ramp's, not a
 * colour pair's.
 */
export function pairHasPet(modalities: ReadonlyArray<string>): boolean {
    return modalities.some((modality) => modality === Modality.PT);
}

/**
 * The blend a pair of these modalities may hold: the record as it is,
 * unless it names a checkerboard for a pair with a PET image, which the
 * renderer draws only over a blend.
 */
export function blendForPair(
    blend: BlendSettings,
    modalities: ReadonlyArray<string>
): BlendSettings {
    return blend.mode === 'checkerboard' && pairHasPet(modalities)
        ? { ...blend, mode: 'blend' }
        : blend;
}

/**
 * Everything that governs how one fused pair is drawn together. Held per
 * pair, so two fused pairs keep two independent settings.
 */
export interface BlendSettings {
    /** The primary's share of the blend, 0..1; the secondary shows the rest. */
    readonly alpha: number;
    readonly mode: FusionViewMode;
    /** One checkerboard tile's edge on the image plane, in mm. */
    readonly checkerboardTileSizeMm: number;
    /** Id of the colour pair for a non-PET fusion, from {@link FUSION_COLOR_MAPPINGS}. */
    readonly colorMappingId: string;
    /** Id of the PET ramp when either image is PET, from {@link PET_COLOR_MAPS}. */
    readonly petColorMapId: number;
}

/** The blend a pair starts with: an even mix, untinted, drawn through the default PET ramp. */
export const DEFAULT_BLEND_SETTINGS: BlendSettings = {
    alpha: 0.5,
    mode: 'blend',
    checkerboardTileSizeMm: DEFAULT_CHECKERBOARD_TILE_SIZE_MM,
    colorMappingId: grayscaleFusionColorMapping.id,
    petColorMapId: defaultPetColorMap.id,
};

/**
 * Refuses a blend change that names a value no control can produce: an
 * alpha outside 0..1, a tile that is not a positive length, or a colour
 * id no picker offers. Throws with the offending field; nothing is
 * clamped. A field left out of the patch is not checked.
 */
export function assertValidBlendPatch(patch: Partial<BlendSettings>): void {
    if (
        patch.alpha !== undefined &&
        !(Number.isFinite(patch.alpha) && patch.alpha >= 0 && patch.alpha <= 1)
    ) {
        throw new Error(`Blend alpha must be within 0..1, got ${patch.alpha}.`);
    }
    if (patch.checkerboardTileSizeMm !== undefined) {
        assertValidCheckerboardTileSizeMm(patch.checkerboardTileSizeMm);
    }
    if (patch.colorMappingId !== undefined) {
        fusionColorMappingById(patch.colorMappingId);
    }
    if (patch.petColorMapId !== undefined) {
        petColorMapById(patch.petColorMapId);
    }
}
